import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { format, parse } from 'date-fns';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { View } from 'react-native';
import Modal from 'react-native-modal';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { z } from 'zod';

import type { CreateCustomerErrorBody } from '@/api/partners/organizations/use-create-customer';
import { useCreateCustomer } from '@/api/partners/organizations/use-create-customer';
import { InputPhone } from '@/components/input-phone';
import type { PhoneCountry } from '@/components/input-phone/types';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { validatePhoneNumber } from '@/core/utils';
import { Button, ControlledInput, showErrorMessage, Text } from '@/ui';

const addCustomerSchema = z.object({
  firstName: z.string().min(2, 'Minimum 2 characters'),
  lastName: z.string().min(2, 'Minimum 2 characters'),
  dob: z
    .string()
    .regex(
      /^\d{2}\/\d{2}\/\d{4}$/,
      'Date of birth must be in the format MM/DD/YYYY'
    ),
  email: z.string().email(),
});

type AddCustomerFormType = z.infer<typeof addCustomerSchema>;

// 422 bodies bypass the camelize interceptor, so keys are snake_case.
const FORM_FIELD_BY_ERROR_KEY: Record<string, keyof AddCustomerFormType> = {
  first_name: 'firstName',
  last_name: 'lastName',
  dob: 'dob',
  email: 'email',
};

type Props = {
  isVisible: boolean;
  onClose: () => void;
};

export const AddCustomerModal = ({ isVisible, onClose }: Props) => {
  const { styles, theme } = useStyles(stylesheet);
  const queryClient = useQueryClient();

  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState<PhoneCountry>({ callingCode: '1' });
  const [phoneError, setPhoneError] = useState<string | null>(null);

  const { mutateAsync: createCustomer, isPending } = useCreateCustomer();

  const { handleSubmit, control, trigger, setValue, setError, reset } =
    useForm<AddCustomerFormType>({
      defaultValues: {
        firstName: '',
        lastName: '',
        dob: '',
        email: '',
      },
      resolver: zodResolver(addCustomerSchema),
    });

  const handleClose = () => {
    reset();
    setPhone('');
    setCountry({ callingCode: '1' });
    setPhoneError(null);
    onClose();
  };

  const onSubmit = async (data: AddCustomerFormType) => {
    setPhoneError(null);

    if (phone && !validatePhoneNumber(country, phone)) {
      setPhoneError('Please enter a valid phone number.');
      return;
    }

    const dobDateObject = parse(data.dob, 'MM/dd/yyyy', new Date());

    try {
      await createCustomer({
        firstName: data.firstName,
        lastName: data.lastName,
        dob: format(dobDateObject, 'yyyy-MM-dd'),
        email: data.email,
        phone: phone ? `+${country.callingCode}${phone}` : undefined,
      });

      queryClient.invalidateQueries({
        queryKey: ['internal/partners/organizations/customers'],
      });

      Toast.show({
        type: 'success',
        text1: 'Customer added successfully.',
        visibilityTime: 4000,
      });

      handleClose();
    } catch (err) {
      const errors = (err as AxiosError<CreateCustomerErrorBody>).response?.data
        ?.errors;

      if (errors && !Array.isArray(errors)) {
        let handledInline = false;

        Object.entries(errors).forEach(([key, message]) => {
          if (key === 'phone') {
            setPhoneError(message);
            handledInline = true;
          } else if (FORM_FIELD_BY_ERROR_KEY[key]) {
            setError(FORM_FIELD_BY_ERROR_KEY[key], { message });
            handledInline = true;
          }
        });

        if (!handledInline) {
          showErrorMessage(Object.values(errors).join(', '));
        }

        return;
      }

      showErrorMessage(
        Array.isArray(errors) ? errors.join(', ') : 'Failed to add customer'
      );
    }
  };

  return (
    <Modal
      animationIn="fadeIn"
      animationInTiming={200}
      animationOut="fadeOut"
      animationOutTiming={200}
      isVisible={isVisible}
      hasBackdrop
      onBackdropPress={handleClose}
      style={styles.modal}
    >
      <View style={styles.container}>
        <View style={styles.header}>
          <Text type="title2">Add Customer</Text>

          <NavigationButton
            type={NavigationButtonTypes.cancel}
            onPress={handleClose}
          />
        </View>

        <View style={styles.body}>
          <View style={styles.nameRow}>
            <View style={{ flex: 1 }}>
              <ControlledInput
                control={control}
                name="firstName"
                label="First name *"
                placeholder="Enter first name"
                onBlur={() => {
                  trigger('firstName');
                }}
                onChangeText={(text: string) => {
                  setValue('firstName', text);
                  trigger('firstName');
                }}
              />
            </View>

            <View style={{ flex: 1 }}>
              <ControlledInput
                control={control}
                name="lastName"
                label="Last name *"
                placeholder="Enter last name"
                onBlur={() => {
                  trigger('lastName');
                }}
                onChangeText={(text: string) => {
                  setValue('lastName', text);
                  trigger('lastName');
                }}
              />
            </View>
          </View>

          <ControlledInput
            control={control}
            name="dob"
            label="Date of birth - mm/dd/yyyy *"
            placeholder="10/28/1996"
            onBlur={() => {
              trigger('dob');
            }}
            onChangeText={(text: string) => {
              setValue('dob', text);
              trigger('dob');
            }}
          />

          <ControlledInput
            control={control}
            name="email"
            label="Email *"
            placeholder="Enter email address"
            autoComplete="email"
            spellCheck={false}
            onBlur={() => {
              trigger('email');
            }}
            onChangeText={(text: string) => {
              setValue('email', text);
              trigger('email');
            }}
          />

          <View style={{ gap: 8 }}>
            <InputPhone
              onChangeText={(text: string) => {
                setPhone(text);
                setPhoneError(null);
              }}
              onCountryChange={(c: PhoneCountry) => {
                setCountry(c);
              }}
            />

            {phoneError ? (
              <Text
                type="caption1"
                style={{ color: theme.colors.input.error.helperText }}
              >
                {phoneError}
              </Text>
            ) : null}
          </View>
        </View>

        <View style={styles.footer}>
          <Button
            text="Cancel"
            type="ghost"
            onPress={handleClose}
            style={{ width: 'auto' }}
          />

          <Button
            text="Add Customer"
            type="primary"
            loading={isPending}
            onPress={handleSubmit(onSubmit)}
            style={{ width: 'auto', minWidth: 150 }}
          />
        </View>
      </View>
    </Modal>
  );
};

const stylesheet = createStyleSheet((theme) => ({
  modal: {
    margin: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  container: {
    width: { xs: '95%', md: 568 },
    borderRadius: 16,
    borderWidth: 1,
    borderColor: theme.colors.dialog.borderColor,
    backgroundColor: theme.colors.dialog.backgroundColor,
    gap: 24,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.card.default.borderColor,
  },
  body: {
    paddingHorizontal: 16,
    gap: 16,
  },
  nameRow: {
    flexDirection: 'row',
    gap: 16,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 8,
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: theme.colors.card.default.borderColor,
  },
}));
